import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';

import { PlayerMode } from '@/components/app/contentPlayer/MediaPlayerProvider/types';
import { calmLogger } from '@/utils/calmLogger';

import { Scene } from './useSelectedScene';

const SCENE_VOLUME_KEY = 'sceneVolumeKey';

export const useSelectedSceneAudio = ({
	selectedScene,
	playerMode,
	isSessionActive,
}: {
	selectedScene?: Scene;
	playerMode: PlayerMode;
	isSessionActive: boolean;
}) => {
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [sceneVolume, setSceneVolume] = useState(0);
	// The scene stays silent for the whole session, paused or playing. Browsers count any playing
	// element as the page playing, so ambient audio would leave Media Session controls stuck on Pause.
	const shouldPauseSceneAudio = playerMode === 'playing' || (playerMode === 'paused' && isSessionActive);

	const onPlay = useCallback(async () => {
		if (!audioRef.current) return;

		try {
			if (audioRef.current.paused) {
				await audioRef.current.play();
			}
		} catch (err) {
			// this is likely going to fail on first load
			// browsers don't allow audio to autoplay
		}
	}, []);

	const onPause = useCallback(() => {
		if (!audioRef.current) return;

		audioRef.current.pause();
	}, []);

	// Pause the scene when a session starts and resume it when the session ends
	useEffect(() => {
		if (shouldPauseSceneAudio) {
			onPause();
		} else {
			onPlay().catch(error => calmLogger.error('Error in useSelectedSceneAudio onPlay', {}, error));
		}
	}, [onPause, onPlay, shouldPauseSceneAudio]);

	// Once client loads, set scene volume
	useEffect(() => {
		const initialSceneVolume = localStorage.getItem(SCENE_VOLUME_KEY)
			? Number(localStorage.getItem(SCENE_VOLUME_KEY))
			: 1;
		// TODO: refactor this code to follow best practices
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setSceneVolume(initialSceneVolume);
	}, []);

	// Update audioRef volume on volume change
	useEffect(() => {
		if (audioRef.current) {
			audioRef.current.volume = sceneVolume;
			localStorage.setItem(SCENE_VOLUME_KEY, sceneVolume.toString());
		}
	}, [sceneVolume]);

	const shouldStartSceneAudio = useEffectEvent(() => !shouldPauseSceneAudio);

	// Init scene audio, only when the scene changes so pausing and resuming keeps the loop's position
	useEffect(() => {
		const initSceneAudio = async () => {
			onPause();
			audioRef.current = null;
			const sceneAudioUrl = selectedScene?.audio.url;

			audioRef.current = new Audio(sceneAudioUrl);
			audioRef.current.loop = true;
			audioRef.current.volume = Number(localStorage.getItem(SCENE_VOLUME_KEY));

			if (shouldStartSceneAudio()) {
				await onPlay();
			}
		};

		initSceneAudio().catch(error =>
			calmLogger.error('Error in useSelectedSceneAudio initSceneAudio', {}, error),
		);
	}, [onPause, onPlay, selectedScene?.audio.url]);

	// Clear scene audio on unmount
	useEffect(() => {
		return () => onPause();
	}, [onPause]);

	return {
		sceneVolume,
		setSceneVolume,
		shouldPauseSceneAudio,
	};
};
